import React, { Fragment, useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import dayjs from "dayjs";
import { Col, Container, Row } from "react-bootstrap";
import { Button } from "@velove/design-system";
import { Divider, Textarea } from "theme-ui";
import { AppContext } from "../../contexts/AppContext";
import { UserDeliveryRequirements } from "../../contexts/contextTypes";
import TrackingHistory from "../TrackingHistory";

// @ts-ignore
import ReactStars from "react-rating-stars-component";
import CustomerSupport from "../CustomerSupport";

const FeedBack = () => {
  const queryClient = useQueryClient();
  const { packageId } = useParams();
  const { t } = useTranslation();
  const { settings, trackingDetails } = useContext(AppContext);
  const [rateNo, setRateNo] = useState(0);
  const [reasons, setReasons] = useState<number[]>([]);
  const [comment, setComment] = useState("");
  const [feedbackId, setFeedbackId] = useState<string | null>(null);
  let [count, setCount] = useState(1);

  const ratingProps = {
    size: 45,
    count: 5,
    color: "black",
    a11y: true,
    isHalf: false,
    emptyIcon: (
      <i
        className="fa fa-star"
        style={{
          color: settings?.brand?.color || "#6F7DFF",
          stroke: "white",
          strokeWidth: "30px",
          marginRight: "5px",
        }}
      />
    ),
    halfIcon: <i className="fa fa-star-half-alt" />,
    filledIcon: <i className="fa fa-star" style={{ marginRight: "5px" }} />,
  };

  const feedBackMutation = useMutation({
    mutationFn: (feedbackData: {
      taskId: string;
      ratingNumber: number;
      reasons: string | null;
      comment: string | null;
    }) => {
      return axios.post(
        `${process.env.REACT_APP_API_ENDPOINT}/v1/package/${packageId}/feedback`,
        feedbackData,
        {
          headers: {
            "content-type": "application/json",
            accept: "application/json",
          },
        }
      );
    },
    onSuccess: ({ data }) => {
      setFeedbackId(data.feedbackId);
      queryClient.invalidateQueries({
        queryKey: ["trackingDetails", packageId],
      });
    },
    onError: () => console.log("Add consumer feedback failed"),
  });

  return (
    <Fragment>
      {!(trackingDetails?.feedbackId || feedbackId) ? (
        <Container>
          <Row className="feedback_container">
            <Col
              className="feedback_header"
              style={{ display: "flex", alignItems: "center" }}
            >
              <Row>
                <Col xxs={12} md={12} lg={12} align="left">
                  <div className="feedback_heading_text">
                    {trackingDetails?.package?.userDeliveryRequirements ===
                    UserDeliveryRequirements.CONTACTLESS
                      ? t("feedback.heading_contactless")
                      : t("feedback.heading")}
                  </div>
                  <div className="feedback_text">
                    {t("feedback.sub_heading")}
                  </div>
                </Col>
                <Col xxs={12} md={12} lg={12} align="center">
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <ReactStars
                      {...ratingProps}
                      value={rateNo}
                      activeColor={"white"}
                      onChange={(newValue: number) => {
                        setRateNo(newValue);
                        setReasons([]);
                      }}
                    />
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
          {rateNo > 0 && (
            <Fragment>
              <Row className="feedback_content" style={{ marginTop: "10px" }}>
                <Col>
                  <Row>
                    <Col>
                      <h5 className="feedback_form-label">
                        {t("feedback.reason_label." + rateNo)}
                      </h5>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      {settings?.feedbackSuggestions.map(
                        (suggestion, index) => {
                          if (!suggestion.rates.includes(rateNo)) return null;

                          return (
                            <Button
                              key={suggestion.key}
                              className="feedback_reason--button"
                              style={{
                                color: reasons.includes(suggestion.key)
                                  ? "black"
                                  : "gray",
                                borderColor: reasons.includes(suggestion.key)
                                  ? "black"
                                  : "lightgray",
                              }}
                              onClick={() => {
                                const index = reasons.indexOf(suggestion.key);
                                if (index > -1) {
                                  reasons.splice(index, 1);
                                } else {
                                  reasons.push(suggestion.key);
                                }

                                setReasons(reasons);
                                setCount(++count);
                              }}
                            >
                              {t("feedback.reasons." + suggestion.key)}
                            </Button>
                          );
                        }
                      )}
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="feedback_content">
                <Col>
                  <Row>
                    <Col>
                      <h5 className="feedback_form-label">
                        {t("feedback.comment_label")}
                      </h5>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <Textarea
                        id="note"
                        rows={6}
                        variant="noShadow"
                        className="feedback_form--text-area"
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className="feedback_content">
                <Col>
                  <Button
                    className="feedback_button"
                    onClick={() => {
                      if (trackingDetails?.task?.id) {
                        feedBackMutation.mutate({
                          taskId: trackingDetails.task.id,
                          ratingNumber: rateNo,
                          reasons:
                            reasons.length > 0
                              ? reasons.filter((reason) => reason).join()
                              : null,
                          comment: comment ? comment : null,
                        });
                      } else {
                        console.log("Task Id is not found.");
                      }
                    }}
                    disabled={
                      !!trackingDetails?.feedbackId ||
                      feedBackMutation.isLoading
                    }
                    loading={feedBackMutation.isLoading}
                    sx={{
                      opacity:
                        !!trackingDetails?.feedbackId ||
                        feedBackMutation.isLoading
                          ? 0.5
                          : 1,
                    }}
                  >
                    {t("feedback.submit")}
                  </Button>
                </Col>
              </Row>
              <Divider />
            </Fragment>
          )}
          <TrackingHistory />
          {settings?.enableCustomerSupportForSuccessfulDeliveries &&
            trackingDetails?.task?.completedAt &&
            (dayjs(trackingDetails.task.completedAt)
              .add(2, "day")
              .isSame(dayjs()) ||
              dayjs(trackingDetails.task.completedAt)
                .add(2, "day")
                .isAfter(dayjs())) && <CustomerSupport />}
        </Container>
      ) : (
        <Container className="feedback_success--container">
          <Row>
            <Col align="center" style={{ width: "100%" }}>
              <img className="feedback_success--logo" alt="feedback_logo" />
            </Col>
          </Row>
          <br />
          <Row>
            <Col align="center" className="feedback_success--label">
              <h1 style={{ marginBottom: "50px" }}>
                {t("feedback.success_header")}
              </h1>
              <h5>{t("feedback.success_message")}</h5>
              <h6>{t("feedback.success_sub_message")}</h6>
            </Col>
          </Row>
        </Container>
      )}
    </Fragment>
  );
};

export default FeedBack;
